import React, { useState, useEffect } from 'react';
import { Divider, Typography } from 'antd';
import { Launch } from '@carbon/icons-react';
import PageMeta from 'components/PageMeta/PageMeta';
import MessagesSection from './__components/MessagesSection';
import ChecklistItems from './__components/ChecklistItems';
import AdditionalItemsSection from './__components/AdditionalItemsSection';
import StatusBanner from './__components/StatusBanner';
import ProgramDetailsCard from './__components/ProgramDetailsCard';
import { useAtom } from 'jotai';
import { checklistItemsAtom, checklistStatusAtom, programDetailsAtom } from '../../atoms';
import legacyService from 'services/legacy.service';
import familyService from 'services/family.service';

const { Title } = Typography;

export function FamilyApplicationSubmitted({ application, household, setStep, readOnly = false, baseRoute }) {
  const [items, setItems] = useAtom(checklistItemsAtom);
  const [programDetails, setProgramDetails] = useAtom(programDetailsAtom);
  const [statusData, setStatusData] = useAtom(checklistStatusAtom);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    setProgramDetails(null);
    setItems(null);
  }, [application?.id, setProgramDetails, setItems]);

  useEffect(() => {
    if (!application?.id) {
      setLoading(false);
      return;
    }

    const fetchAll = async () => {
      try {
        setLoading(true);
        const promises = [];

        if (items === null) {
          promises.push(legacyService.getChecklistItems(application.id).then((data) => setItems(data || [])));
        }

        if (programDetails === null) {
          promises.push(familyService.getProgramDetails(application.id).then((data) => setProgramDetails(data)));
        }

        await Promise.all(promises);
      } catch (error) {
        console.error('Error fetching checklist data:', error);
        if (items === null) setItems([]);
      } finally {
        setLoading(false);
      }
    };

    if (items !== null && programDetails !== null) {
      setLoading(false);
      return;
    }

    fetchAll();
  }, [application?.id, items, programDetails, setItems, setProgramDetails]);

  useEffect(() => {
    if (!application?.id) {
      setStatusLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        setStatusLoading(true);
        const data = await legacyService.getChecklistStatus(application.id);
        setStatusData(data);
      } catch (error) {
        console.error('Error fetching checklist status:', error);
      } finally {
        setStatusLoading(false);
      }
    };

    fetchStatus();
  }, [application?.id, setStatusData]);

  return (
    <>
      <PageMeta title="My checklist" />

      <div className="max-w-[1024px] mx-auto p-6">
        <Title level={1} className="!text-4xl !font-bold mb-8">
          My checklist
        </Title>

        {/* Status banner — driven by appStatus from /checklist/status */}
        <StatusBanner statusData={statusData} loading={statusLoading} />

        {/* Blue-bordered system message */}
        <MessagesSection application={application} loading={loading} />

        <Divider className="my-10" />

        {/* Program details acknowledgement card */}
        <ProgramDetailsCard applicationId={application?.id} />

        {/* Checklist Items Area */}
        <ChecklistItems applicationId={application?.id} loading={loading} />

        {/* Have something not listed above? - Dashed border, collapsed by default */}
        <AdditionalItemsSection applicationId={application?.id} />

        <Divider className="my-8" />

        <a
          href="https://forms.office.com/Pages/ResponsePage.aspx?id=DLfJPWfkWkK24PKxgY3DCv9N5ZyIKKtPt6W_ebsggIpUQjYwWVdTN0g4TkZSTEdZNFNPR05NRjQ4WSQlQCN0PWcu"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 !text-[#5b6f93] !hover:text-[#5b6f93] hover:underline"
        >
          Share quick feedback (2 minutes)
          <Launch />
        </a>
      </div>
    </>
  );
}
